/**
 * MetaPixelProvider - React component for automatic page tracking
 * 
 * Wraps the app and automatically tracks:
 * - PageView on route changes
 * - Initializes pixel when marketing consent is given
 */

import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { initMetaPixel, trackPageView, hasMarketingConsent } from '../lib/metaPixel';

const MetaPixelProvider = ({ children }) => {
  const location = useLocation();
  
  // Initialize pixel if consent exists
  const checkAndInitPixel = useCallback(() => {
    console.log('[MetaPixelProvider] Checking consent and initializing...');
    if (hasMarketingConsent()) {
      const initialized = initMetaPixel();
      console.log('[MetaPixelProvider] Pixel initialized:', initialized);
      return initialized;
    }
    console.log('[MetaPixelProvider] No marketing consent');
    return false;
  }, []);
  
  // Track page views on route change
  useEffect(() => {
    console.log('[MetaPixelProvider] Route changed to:', location.pathname);
    
    if (hasMarketingConsent()) {
      initMetaPixel();
      const eventId = trackPageView(location.pathname);
      console.log('[MetaPixelProvider] PageView tracked, eventId:', eventId);
    }
  }, [location.pathname]);
  
  // Initialize on mount
  useEffect(() => {
    console.log('[MetaPixelProvider] Component mounted');
    checkAndInitPixel();
  }, [checkAndInitPixel]);
  
  // Listen for consent changes
  useEffect(() => {
    const handleConsentChange = (e) => {
      console.log('[MetaPixelProvider] Consent changed:', e.detail);
      if (e.detail?.marketing) {
        initMetaPixel();
        trackPageView(location.pathname);
      }
    };
    
    const handleStorageChange = (e) => {
      if (e.key === 'smartballoon-cookie-consent') {
        console.log('[MetaPixelProvider] Storage changed');
        checkAndInitPixel();
        if (hasMarketingConsent()) {
          trackPageView(location.pathname);
        }
      }
    };
    
    // Listen for custom consent event
    window.addEventListener('cookieConsentChanged', handleConsentChange);
    
    // Listen for storage changes (cross-tab)
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAndInitPixel, location.pathname]);
  
  return <>{children}</>;
};

export default MetaPixelProvider;
